import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Master,
  ECommonDialogType,
  EFeatureStatus,
  EPageName,
  ETabName,
  TabList,
  EPageType,
} from '../data/Constants';
import { useLocation, useNavigate, Outlet } from 'react-router';
import ActionPanel from './ActionPanel';
import BreadcrumbBar from './BreadcrumbBar';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, AppState } from '../store/AppStore';
import {
  resetIsDataUpdated,
  setActiveWorkItem,
  setEditMode,
  setProductStatus,
} from '../store/states/procos';
import { resetToInitialState } from '../store/states/CategorySlice';
import { resetToInitial } from '../store/states/PdmSlice';
import ConfirmationDialog from './ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { PublishProductData } from './Pdm/PublishProductData';
import DraggableComponent from './DraggableComponent';

import type { IDialogFor, ITab } from '../../types';

/**
 * This component renders the Tab Panel for ProCoS and PDM section
 * @returns {JSX.Element} a  App tabs component.
 */
const AppTabs = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const productId = useSelector((state: AppState) => state.procos.product.id);
  const productStatusInMaster = useSelector(
    (state: AppState) => state.procos.productStatusInMaster
  );

  const [confirmWarning, setConfirmWarning] = useState({
    open: false,
    title: 'dialogBox.unsavedChanges',
    description: 'messages.cancel.homeWarning',
    alertFor: ECommonDialogType.HomeClickWarning,
  });

  const [isLandingPage, setIsLandingPage] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [tabs, setTabs] = useState<ITab[]>(TabList.Home);
  const [showPublishDialog, setShowPublishDialog] = useState<boolean>(false);
  const isDataUpdated = useSelector((state: AppState) => {
    if (
      state.procos.isDataUpdated ||
      state.pdm.isModified ||
      state.category.isCategoryDataModified
    ) {
      return true;
    } else {
      return false;
    }
  });

  const saveProcosInBatchesInProgress = useSelector((state: AppState) => {
    return state.procos.isCreateRuleInvoked && !state.procos.isBatchSaveComplete;
  });

  //Show warning message while navigating between tabs
  const handleChangeTab = (
    e: React.SyntheticEvent<Element, Event>,
    newTab: number,
    tabsArray: { id: string; name: string; path: string }[]
  ) => {
    if (isDataUpdated) {
      e.preventDefault();

      if (activeTab !== newTab) {
        setActiveTab(newTab);

        // Allow switch tabs when  save rule is processing else show the warning dialog
        if (saveProcosInBatchesInProgress) {
          navigate(tabsArray[newTab].path);
        } else {
          setConfirmWarning({ ...confirmWarning, open: true });
        }
      }
    } else if (activeTab !== newTab) {
      navigate(tabsArray[newTab].path);
      setActiveTab(newTab);
    }
  };

  //Reset the data on navigation between tabs
  const resetData = () => {
    if (location.pathname.includes(EPageName.Category)) {
      dispatch(resetToInitialState());
    } else if (location.pathname.includes(EPageName.Pdm)) {
      dispatch(resetToInitial());
    } else {
      //Reset Procos Product Status (Active) and workItem(Master) when navigate to PDM or Category
      dispatch(setProductStatus(EFeatureStatus.Active));
      dispatch(setActiveWorkItem(Master));
      dispatch(setEditMode({ isEdit: false }));
      dispatch(resetIsDataUpdated());
    }
    navigate(tabs[activeTab].path);
  };

  //This method triggers when user clicks on cancel or ok button on confirm dialog
  const onConfirmStatus = (confirm: IDialogFor) => {
    setConfirmWarning({ ...confirmWarning, open: false });
    if (confirm.status) {
      resetData();
    } else {
      setActiveTab(tabs.findIndex((tab) => location.pathname.includes(tab.path)));
    }
  };

  //Set tooltip for Send to Sofon button based on product's status in Master
  const getNotificationTooltipTitle = (): string => {
    return productStatusInMaster === EFeatureStatus.Active
      ? t('tooltip.publishProductDataEnabled')
      : t('tooltip.publishProductDataDisabled');
  };

  useEffect(() => {
    const key = getTabKeyByPath(location.pathname);

    if (key === EPageType.Home) {
      setIsLandingPage(true);
    } else {
      setIsLandingPage(false);
    }

    //Match the location pathname with TabList data and set ActiveTab accordingly.
    let tabIndex = TabList[key as keyof typeof TabList].findIndex((item) =>
      location.pathname.endsWith(item.path)
    );
    if (tabIndex === -1) {
      tabIndex = TabList[key as keyof typeof TabList].findIndex((item) =>
        location.pathname.includes(item.path)
      );
    }
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
    }

    setTabs(TabList[key as keyof typeof TabList]);
  }, [location]);

  /**
   * This method find the current tab key to get the tabs
   * @param {string} path path name
   * @returns {string} return the tab key
   */
  function getTabKeyByPath(path: string): string {
    let key: string = EPageType.Home;
    Object.keys(TabList).some((tabKey: string) => {
      if (TabList[tabKey].some((tab: ITab) => path.includes(tab.id))) {
        key = tabKey;
        return true;
      }
      return false;
    });
    return key;
  }

  return (
    <>
      <Box className="secondaryHeader">
        <>
          {!isLandingPage && <BreadcrumbBar />}
          <ActionPanel />
        </>
      </Box>

      <Divider className="divider" />

      <Box className="secondaryHeaderTabs">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <nav className="navbar procos-tab-panel">
            <Tabs
              className="pdm-tab"
              value={activeTab}
              onChange={(e, newTab) => handleChangeTab(e, newTab, tabs)}
              orientation="horizontal"
              variant="scrollable"
              scrollButtons={false}
              visibleScrollbar
            >
              {tabs.map((s, index) => (
                <Tab
                  key={`${s.id}-${index}`}
                  data-testid={s.id}
                  id={s.id}
                  label={<span>{t(`${s.name}`)}</span>}
                  value={index}
                  className="pdm-tabs"
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </nav>
        </Box>

        {tabs[activeTab].id === ETabName.PDM && (
          <Tooltip title={getNotificationTooltipTitle()}>
            <span>
              <Button
                data-testid="send-to-sofon-button"
                className="p-5px mr-5px text-capitalize common-btn h-3-5"
                color="primary"
                variant="contained"
                onClick={() => setShowPublishDialog(true)}
                disabled={productStatusInMaster !== EFeatureStatus.Active}
              >
                {t('button.publishProductData')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Divider className="divider" />
      <Outlet />
      {confirmWarning.open && (
        <ConfirmationDialog dialogInfo={confirmWarning} onConfirmAction={onConfirmStatus} />
      )}
      {showPublishDialog && (
        <Dialog
          open={showPublishDialog}
          aria-labelledby="publish-product-data-dialog"
          PaperComponent={DraggableComponent}
          className="common-dialog-style alert-dialog manipulation-dialog"
        >
          <DialogTitle className="header cursor-move">{t('button.publishProductData')}</DialogTitle>
          <DialogContent className="text-left p-0">
            <PublishProductData productId={productId} onClose={() => setShowPublishDialog(false)} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AppTabs;
